<template>
    <AppDialog v-model="isOpen" size="sm">
        <template #header>
            <h2 class="text-lg font-semibold text-theme-base">
                {{ title }}
            </h2>
        </template>

        <div v-if="preview" class="space-y-3 text-sm text-theme-base">
            <template v-if="preview.changeType === 'upgrade'">
                <p>
                    Tu plan cambia hoy a <strong>{{ planName }}</strong
                    >. Pagas
                    <strong>{{ preview.amount | currency }}</strong> por el
                    primer {{ periodUnit }}.
                </p>
                <p v-if="preview.creditAmount > 0" class="text-theme-muted">
                    Descontamos {{ preview.creditAmount | currency }} de lo que
                    no alcanzaste a usar de tu plan actual.
                </p>
            </template>

            <template v-else-if="isContinue">
                <p>
                    Pagas <strong>{{ preview.amount | currency }}</strong> y
                    sigues con <strong>{{ planName }}</strong> cuando termine tu
                    prueba, el
                    <strong>{{ preview.startsAt | dateSimple }}</strong
                    >.
                </p>
                <p class="text-theme-muted">
                    No pierdes los días de prueba que te quedan: el periodo
                    pagado empieza ese día.
                </p>
            </template>

            <template v-else-if="isFree">
                <p>
                    Desde el
                    <strong>{{ preview.startsAt | dateSimple }}</strong>
                    pasarás al plan Free.
                </p>
                <div v-if="hasLosses" class="flex flex-col gap-1">
                    <p class="font-semibold">Free no incluye:</p>
                    <ul class="list-disc pl-5 flex flex-col gap-1">
                        <li v-for="item in losses.features" :key="item.feature">
                            {{ item.label }}
                        </li>
                        <li
                            v-for="item in losses.resources"
                            :key="item.resourceCode"
                        >
                            Más de {{ item.limit }} {{ item.label }} (tienes
                            {{ item.used }})
                        </li>
                    </ul>
                </div>
                <p class="text-theme-muted">
                    No borramos nada de lo que ya creaste: si vuelves a un plan
                    pago lo puedes activar de nuevo. Hasta esa fecha sigues con
                    tu plan actual.
                </p>
            </template>

            <template v-else>
                <p>
                    Desde el
                    <strong>{{ preview.startsAt | dateSimple }}</strong> tu plan
                    será <strong>{{ planName }}</strong> ({{
                        preview.amount | currency
                    }}
                    al {{ periodUnit }}).
                </p>
                <p class="text-theme-muted">
                    Podrás pagarlo desde el
                    {{ preview.paymentAvailableFrom | date }}. Hasta el cambio
                    sigues con tu plan actual.
                </p>
            </template>
        </div>

        <template #footer>
            <legal-payment-notice v-if="isPayment" class="mb-3" />
            <div class="flex flex-col sm:flex-row justify-end gap-2">
                <button
                    class="rounded-lg border border-theme-border px-4 py-2 text-sm font-medium text-theme-muted transition hover:bg-theme-hover"
                    @click="isOpen = false"
                >
                    Volver
                </button>
                <app-button
                    :color="isFree ? 'danger' : 'brand-blue'"
                    :loading="loading"
                    @click="$emit('confirm')"
                >
                    {{ confirmLabel }}
                </app-button>
            </div>
        </template>
    </AppDialog>
</template>

<script>
import { PLAN_PERIOD_UNITS } from "@/common/lib/plan-periods";

import LegalPaymentNotice from "@/components/legal/LegalPaymentNotice.vue";
import AppDialog from "@/components/utils/AppDialog.vue";

export default {
    name: "PlanChangeConfirmDialog",
    components: { AppDialog, LegalPaymentNotice },
    model: {
        prop: "value",
        event: "input"
    },
    props: {
        value: {
            type: Boolean,
            default: false
        },
        // Respuesta de POST .../mine/change-plan/preview.
        preview: {
            type: Object,
            default: null
        },
        planName: {
            type: String,
            default: ""
        },
        isFree: {
            type: Boolean,
            default: false
        },
        // `listPlanLosses` al pasar a Free: { features, resources }.
        losses: {
            type: Object,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        isOpen: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit("input", value);
            }
        },
        hasLosses() {
            return Boolean(
                this.losses &&
                    (this.losses.features.length ||
                        this.losses.resources.length)
            );
        },
        isContinue() {
            return Boolean(
                this.preview && this.preview.changeType === "continue"
            );
        },
        // "mes" | "trimestre" | "año", según el periodo del plan elegido.
        periodUnit() {
            return (
                PLAN_PERIOD_UNITS[this.preview && this.preview.period] ||
                PLAN_PERIOD_UNITS.monthly
            );
        },
        title() {
            if (this.isContinue) return `Seguir con ${this.planName}`;
            if (this.isFree) return "Cancelar plan";
            return this.preview && this.preview.changeType === "upgrade"
                ? "Subir de plan"
                : "Cambiar de plan";
        },
        isPayment() {
            if (this.isContinue) return true;
            return (
                !this.isFree &&
                !!this.preview &&
                this.preview.changeType === "upgrade"
            );
        },
        confirmLabel() {
            if (this.isContinue) return "Pagar";
            if (this.isFree) return "Cancelar plan";
            return this.preview && this.preview.changeType === "upgrade"
                ? "Pagar y cambiar"
                : "Programar cambio";
        }
    }
};
</script>
